import { useQuery } from '@tanstack/react-query'
import { useState, useEffect, useCallback, lazy, Suspense } from 'react'
import { fetchPersonaState } from '../../lib/api'
import { useAuth } from '../../context/AuthContext'
import { useSSEConnected } from '../../hooks/useSSE'
import PersonaAvatar from '../../components/PersonaAvatar'
import type { PersonaState } from '../../lib/types'

// Lazy-load heavy components
const PersonaHomePage = lazy(() => import('./PersonaHomePage'))
const ConversationViewPage = lazy(() => import('./ConversationViewPage'))
const UserPublicPage = lazy(() => import('./UserPublicPage'))

interface PersonaFull extends PersonaState {
  name: string
  queue_count?: number
  session_type?: string
}

const PERSONA_COLORS: Record<string, string> = {
  forge: '#B25943', mira: '#9C59F5', flux: '#33C7A0', chronicle: '#8C6DBF',
  timbre: '#ED9429', griot: '#4E937A', relay: '#4493D4', sentinel: '#999999',
  cairn: '#7A9E7E', joel: '#59C5FA', glean: '#A89060', wrench: '#8B8B8B',
  ember: '#E06040', lumen: '#F5D623', vector: '#5AA0D0', ledger: '#6B8E70',
  beacon: '#D4A520', anvil: '#7D7D7D',
}

function ContextBar({ pct }: { pct: number }) {
  const color = pct >= 75 ? 'var(--danger)' : pct >= 50 ? 'var(--warning)' : 'var(--accent)'
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-10 h-1 rounded-full overflow-hidden" style={{ background: 'var(--bg-elevated)' }}>
        <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
      </div>
      <span className="text-xs font-mono" style={{ color, fontSize: 9 }}>{pct}%</span>
    </div>
  )
}

// Find or create the persistent 1:1 thread for a persona.
// Defense-in-depth: the server's find_or_create has historically returned
// group chats that happen to contain both participants. Verify the returned
// conversation is actually a 1:1 with the expected persona before handing
// the caller an ID to render — otherwise return null and let the UI show an
// explicit failure rather than silently landing in a group chat.
// Hard 10s timeout on each fetch so a hung server flips the UI to the
// "Could not open chat" retry state rather than Loading-forever (task-013).
async function findHomeChat(personaName: string, userName: string): Promise<string | null> {
  const fetchWithTimeout = (url: string, init: RequestInit, ms = 10000) => {
    const ctl = new AbortController()
    const t = setTimeout(() => ctl.abort(), ms)
    return fetch(url, { ...init, signal: ctl.signal }).finally(() => clearTimeout(t))
  }
  try {
    const token = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetchWithTimeout('/api/conversations', {
      method: 'POST',
      headers,
      body: JSON.stringify({ find_or_create: true, participants: [userName, personaName] }),
    })
    if (!res.ok) return null
    const data = await res.json()
    if (!data.id) return null
    // Re-fetch to verify type and participant set
    const verifyRes = await fetchWithTimeout(`/api/conversations/${data.id}`, { headers })
    if (!verifyRes.ok) return null
    const verifyBody = await verifyRes.json()
    const convo = verifyBody.conversation || verifyBody
    const participants = verifyBody.participants || convo.participants || []
    const selfIds = [userName.toLowerCase(), 'joel']
    const personaLower = personaName.toLowerCase()
    const parts = participants.map((p: { persona?: string; user_id?: string; display_name?: string }) => {
      const id = (p.persona || p.user_id || p.display_name || '').toLowerCase()
      const dn = (p.display_name || '').toLowerCase()
      return { id, displayName: dn }
    })
    const nonSelf = parts.filter((p: { id: string; displayName: string }) => !selfIds.includes(p.id) && !selfIds.includes(p.displayName))
    const isValid = convo.type === '1:1' && parts.length <= 2 && nonSelf.length === 1 &&
      (nonSelf[0].id === personaLower || nonSelf[0].displayName === personaLower || nonSelf[0].id === `pu-${personaLower}`)
    if (!isValid) {
      console.error('[findHomeChat] server returned non-1:1 for persona click', { personaName, userName, convo, participants })
      return null
    }
    return data.id
  } catch { return null }
}

export default function PersonasPage() {
  const { user } = useAuth()
  const sseOk = useSSEConnected()
  const isCommunity = user?.userType === 'community'
  const communityId = user?.communityId || `portal-${user?.communitySlug || ''}`
  const [selected, setSelected] = useState<{ name: string; mode: 'chat' | 'home' | 'user'; communitySlug?: string } | null>(null)
  const [chatId, setChatId] = useState<string | null>(null)
  const [chatFailed, setChatFailed] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem('neighborhood-sidebar') !== '0')

  const saveSidebar = (val: boolean) => {
    setSidebarOpen(val)
    localStorage.setItem('neighborhood-sidebar', val ? '1' : '0')
  }

  const { data, isLoading } = useQuery({
    queryKey: ['personaState'],
    queryFn: fetchPersonaState,
    refetchInterval: sseOk ? false : 30_000, // SSE-conditional polling fallback
  })

  // For community users, fetch their assigned personas to filter the list
  const { data: communityTeam } = useQuery({
    queryKey: ['communityTeam', communityId],
    queryFn: async () => {
      const ids = [...new Set([communityId, `portal-${user?.communitySlug || ''}`])]
      for (const pid of ids) {
        try {
          const res = await fetch(`/api/communities/${pid}/personas`)
          if (!res.ok) continue
          const data = await res.json()
          return new Set(data.personas || [])
        } catch { /* try next */ }
      }
      return new Set<string>()
    },
    enabled: isCommunity,
  })

  const allPersonas = data
    ? Object.entries(data).map(([name, s]) => ({ ...s, name } as PersonaFull)).sort((a, b) => {
        if (a.awake && !b.awake) return -1
        if (!a.awake && b.awake) return 1
        return a.name.localeCompare(b.name)
      })
    : []

  // Community users only see their assigned personas
  const personas = isCommunity && communityTeam ? allPersonas.filter(p => communityTeam.has(p.name)) : allPersonas

  const awake = personas.filter(p => p.awake)
  const sleeping = personas.filter(p => !p.awake)

  // Fetch community users
  const { data: communitiesData } = useQuery({
    queryKey: ['communities-neighborhood'],
    queryFn: async () => {
      const res = await fetch('/api/communities')
      if (!res.ok) return []
      const data = await res.json()
      const communities = (data.communities || data) as { id: string; name: string; slug: string; status: string }[]
      const external = communities.filter(p => !['gathering', 'koralmind', 'lumen-lab'].includes(p.slug))
      const withUsers: { community: typeof communities[0]; users: { id: string; name: string; role?: string }[] }[] = []
      for (const p of external) {
        try {
          const uRes = await fetch(`/api/communities/${p.id}/users`)
          if (uRes.ok) {
            const uData = await uRes.json()
            const users = ((uData.users || uData) as any[]).filter((u: any) => u.role !== 'admin')
            if (users.length > 0) withUsers.push({ community: p, users })
          }
        } catch { /* skip */ }
      }
      return withUsers
    },
    refetchInterval: sseOk ? false : 60_000, // SSE-conditional polling fallback
  })

  // Resolve chat ID when opening chat mode
  useEffect(() => {
    if (selected?.mode === 'chat') {
      setChatId(null)
      setChatFailed(false)
      const chatUser = user?.loginName?.toLowerCase() || user?.userName?.toLowerCase() || 'joel'
      findHomeChat(selected.name, chatUser).then(id => {
        if (id) setChatId(id)
        else setChatFailed(true)
      })
    }
  }, [selected?.name, selected?.mode])

  const handleChat = useCallback((name: string) => {
    setSelected({ name, mode: 'chat' })
    // sidebar stays open
  }, [])

  const handleHome = useCallback((name: string) => {
    setSelected({ name, mode: 'home' })
    // sidebar stays open
  }, [])

  function PersonaRow({ p }: { p: PersonaFull }) {
    const isSelected = selected?.name === p.name
    const color = PERSONA_COLORS[p.name] || '#8b949e'

    return (
      <div
        onClick={() => handleChat(p.name)}
        className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors"
        style={{
          background: isSelected ? 'var(--bg-elevated)' : 'transparent',
          borderLeft: isSelected ? `2px solid ${color}` : '2px solid transparent',
        }}
      >
        <PersonaAvatar name={p.name} size={20} showStatus awake={p.awake} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium truncate" style={{ color: isSelected ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
              {p.name}
            </span>
          </div>
          {p.awake && p.context_pct != null && (
            <ContextBar pct={p.context_pct} />
          )}
        </div>

        {/* Home button */}
        <button
          onClick={(e) => { e.stopPropagation(); handleHome(p.name) }}
          className="shrink-0 rounded-md flex items-center gap-1 transition-colors hover:bg-[var(--bg-elevated)]"
          style={{
            background: isSelected && selected?.mode === 'home' ? `${color}33` : 'none',
            color: 'var(--text-muted)',
            border: 'none', cursor: 'pointer',
            fontSize: 16,
            padding: '4px 8px',
          }}
          title={`Visit ${p.name}'s home`}
        >
          🏠
        </button>
      </div>
    )
  }

  if (isLoading) return <div className="p-6 text-sm" style={{ color: 'var(--text-secondary)' }}>Loading...</div>

  return (
    <div className="flex h-full overflow-hidden">
      {/* ─── Left: Persona sidebar ──────────────── */}
      {sidebarOpen && (
        <div
          className="shrink-0 flex flex-col overflow-hidden"
          style={{ width: 220, borderRight: '1px solid var(--border)', background: 'var(--bg-body)' }}
        >
          {/* Header */}
          <div className="shrink-0 px-2 py-2" style={{ borderBottom: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>Neighborhood</span>
              <button
                onClick={() => saveSidebar(false)}
                className="text-xs px-1 rounded"
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
                title="Collapse"
              >
                ◀
              </button>
            </div>
            <div className="flex items-center gap-3 text-xs" style={{ fontSize: 10 }}>
              <span style={{ color: 'var(--success)' }}>⬤ {awake.length} awake</span>
              <span style={{ color: 'var(--text-muted)' }}>{sleeping.length} sleep</span>
            </div>
          </div>

          {/* Persona list */}
          <div className="flex-1 overflow-y-auto px-1 py-1">
            {awake.length > 0 && (
              <div className="mb-2">
                {awake.map(p => <PersonaRow key={p.name} p={p} />)}
              </div>
            )}
            {sleeping.length > 0 && (
              <>
                <div className="text-xs px-2 py-1" style={{ color: 'var(--text-muted)', fontSize: 9 }}>
                  SLEEPING ({sleeping.length})
                </div>
                {sleeping.map(p => <PersonaRow key={p.name} p={p} />)}
              </>
            )}

            {/* Community users */}
            {(communitiesData || []).map(({ community, users }) => (
              <div key={community.id} className="mt-2">
                <div className="text-xs px-2 py-1" style={{ color: 'var(--text-muted)', fontSize: 9 }}>
                  {community.name.toUpperCase()} ({users.length})
                </div>
                {users.map(u => {
                  const isUserSelected = selected?.name === u.name && selected?.mode === 'user'
                  return (
                    <div
                      key={u.id}
                      onClick={() => setSelected({ name: u.name, mode: 'user', communitySlug: community.slug })}
                      className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors"
                      style={{
                        background: isUserSelected ? 'var(--bg-elevated)' : 'transparent',
                        borderLeft: isUserSelected ? '2px solid var(--accent)' : '2px solid transparent',
                      }}
                    >
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                        style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)', fontSize: 9 }}
                      >
                        {u.name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <span className="text-xs truncate" style={{ color: isUserSelected ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                        {u.name[0].toUpperCase() + u.name.slice(1)}
                      </span>
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Collapsed sidebar toggle */}
      {!sidebarOpen && (
        <div
          onClick={() => saveSidebar(true)}
          className="shrink-0 flex items-center justify-center cursor-pointer hover:bg-[var(--bg-elevated)] transition-colors"
          style={{ width: 24, borderRight: '1px solid var(--border)', background: 'var(--bg-body)' }}
          title="Expand sidebar"
        >
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>▶</span>
        </div>
      )}

      {/* ─── Right: Content area ──────────────── */}
      <div className="flex-1 overflow-hidden" style={{ background: 'var(--bg-body)' }}>
        {selected ? (
          selected.mode === 'chat' && chatId ? (
            <Suspense fallback={<div className="p-6 text-sm" style={{ color: 'var(--text-muted)' }}>Loading chat...</div>}>
              <ConversationViewPage overrideId={chatId} />
            </Suspense>
          ) : selected.mode === 'home' ? (
            <Suspense fallback={<div className="p-6 text-sm" style={{ color: 'var(--text-muted)' }}>Loading home...</div>}>
              <PersonaHomePage overrideName={selected.name} />
            </Suspense>
          ) : selected.mode === 'user' && selected.communitySlug ? (
            <Suspense fallback={<div className="p-6 text-sm" style={{ color: 'var(--text-muted)' }}>Loading profile...</div>}>
              <UserPublicPage overrideCommunitySlug={selected.communitySlug} overrideUserId={selected.name} />
            </Suspense>
          ) : selected.mode === 'chat' && chatFailed ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Could not open chat with {selected.name}</div>
                <button
                  onClick={() => { setChatFailed(false); setChatId(null); const chatUser = user?.userId || user?.loginName || user?.userName || 'joel'; findHomeChat(selected.name, chatUser).then(id => { if (id) setChatId(id); else setChatFailed(true) }) }}
                  className="mt-2 text-xs px-3 py-1 rounded"
                  style={{ background: 'var(--bg-elevated)', color: 'var(--accent)', border: '1px solid var(--border)', cursor: 'pointer' }}
                >
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Loading...</div>
            </div>
          )
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-2xl mb-2" style={{ opacity: 0.15 }}>🏘</div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Click a name to chat, or click 🏠 to visit their home
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
